import { Upload } from 'lucide-react';
import { lazy, Suspense, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Button } from '~/components/ui/button';
import { Card, CardContent } from '~/components/ui/card';
import { Separator } from '~/components/ui/separator';
import { Skeleton } from '~/components/ui/skeleton';
import { cn } from '~/utils/cn';
import { downloadCsv } from '~/utils/csv';

import { useExperimentInStockTrends } from '../../hooks/use-experiments';
import type { HealthStatTone } from '../../types/experiment.types';

import { ExperimentDataUnavailable } from './experiment-data-unavailable';
import type { KpiTone } from './experiment-kpi-card';
import { ExperimentKpiCard } from './experiment-kpi-card';
import { ExperimentSeriesToggle } from './experiment-series-toggle';
import type { TrendSeries } from './experiment-trend-chart';
import { ExperimentTrendEmptyState } from './experiment-trend-empty-state';

// Lazy-loaded — keeps echarts out of this panel's chunk so the surrounding
// KPI cards paint without blocking on the chart bundle.
const ExperimentTrendChart = lazy(() =>
  import('./experiment-trend-chart').then((m) => ({ default: m.ExperimentTrendChart })),
);

interface Props {
  experimentId: string;
}

const SERIES_META = [
  { id: 'test', color: 'blue' as const, lineStyle: 'solid' as const, labelKey: 'testInStock' },
  {
    id: 'control',
    color: 'orange' as const,
    lineStyle: 'dashed' as const,
    labelKey: 'controlInStock',
  },
];

const toneMap: Record<HealthStatTone, KpiTone> = {
  default: 'default',
  success: 'success',
  warning: 'warning',
  error: 'error',
};

// In-stock rate is a magnitude (0..1), not a lift delta — so this stays
// distinct from the shared lift formatters: no sign prefix, no near-zero
// collapse. Rounds to whole percent.
function formatPct(value: number): string {
  if (!Number.isFinite(value)) return '—';
  const pct = Math.round(value * 100);
  return `${String(pct)}%`;
}

export function ExperimentInStockTrends({ experimentId }: Props) {
  const { t } = useTranslation('experiments');
  const { data, isLoading, isError } = useExperimentInStockTrends(experimentId);

  const [visibleSeriesIds, setVisibleSeriesIds] = useState(['test', 'control']);

  const xAxisCategories = useMemo(
    () => (data ? data.series.map((p) => `Day ${String(p.day)}`) : []),
    [data],
  );

  const series = useMemo<TrendSeries[]>(() => {
    if (!data) return [];
    return SERIES_META.map((meta) => ({
      id: meta.id,
      name: t(`detail.health.inStockTrends.series.${meta.labelKey}`),
      color: meta.color,
      lineStyle: meta.lineStyle,
      points: data.series.map((p) => ({
        x: `Day ${String(p.day)}`,
        y: p[meta.id as 'test' | 'control'],
      })),
    }));
  }, [data, t]);

  const toggleSeries = (id: string, checked: boolean) => {
    setVisibleSeriesIds((prev) =>
      checked
        ? [...prev, id].filter((v, i, arr) => arr.indexOf(v) === i)
        : prev.filter((v) => v !== id),
    );
  };

  // Exports the underlying per-day series (not just the visible toggled lines).
  const handleExport = () => {
    if (!data || data.series.length === 0) return;
    const csvRows = data.series.map((point) => ({
      day: point.day,
      test: formatPct(point.test),
      control: formatPct(point.control),
    }));
    downloadCsv('in-stock-trends', csvRows, [
      { key: 'day', label: t('detail.dailyLift.columns.day', 'Day') },
      { key: 'test', label: t('detail.health.inStockTrends.series.testInStock') },
      { key: 'control', label: t('detail.health.inStockTrends.series.controlInStock') },
    ]);
  };

  return (
    <Card className="min-w-0 overflow-hidden">
      <CardContent className="min-w-0 space-y-6 p-6">
        {/* Header */}
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div className="flex min-w-0 flex-1 flex-col">
            <h3 className="text-[16px] font-medium wrap-break-word">
              {t('detail.health.inStockTrends.title')}
            </h3>
            <p className="text-muted-foreground text-sm sm:wrap-break-word">
              {t('detail.health.inStockTrends.subtitle')}
            </p>
          </div>
          <Button
            variant="outline"
            onClick={handleExport}
            disabled={isLoading || isError || !data || data.series.length === 0}
            className={cn(
              'gap-2',
              isLoading || isError || !data || data.series.length === 0
                ? 'cursor-not-allowed'
                : 'cursor-pointer',
            )}
          >
            <Upload className="h-4 w-4" aria-hidden="true" />
            {t('detail.health.inStockTrends.exportCsv')}
          </Button>
        </div>

        {/* KPI cards + chart — collapse to a "data unavailable" line when the
            source query throws (no backend endpoint maps to this shape). */}
        {isError ? (
          <ExperimentDataUnavailable />
        ) : (
          <>
            <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-4">
              {isLoading || !data
                ? Array.from({ length: 4 }, (_, i) => (
                    <Card key={i}>
                      <CardContent className="space-y-2 p-5">
                        <Skeleton className="h-8 w-20" />
                        <Skeleton className="h-4 w-28" />
                      </CardContent>
                    </Card>
                  ))
                : data.kpis.map((k) => (
                    <ExperimentKpiCard
                      key={k.label}
                      label={k.label}
                      value={k.value}
                      {...(k.hint ? { hint: k.hint } : {})}
                      tone={k.tone ? toneMap[k.tone] : 'default'}
                      className="bg-selected-row border-transparent shadow-none"
                    />
                  ))}
            </div>

            <Separator />

            {/* Series toggles */}
            <div className="flex justify-end">
              <div className="flex flex-wrap gap-2">
                {SERIES_META.map((meta) => (
                  <ExperimentSeriesToggle
                    key={meta.id}
                    color={meta.color}
                    label={t(`detail.health.inStockTrends.series.${meta.labelKey}`)}
                    checked={visibleSeriesIds.includes(meta.id)}
                    onCheckedChange={(v) => {
                      toggleSeries(meta.id, v);
                    }}
                  />
                ))}
              </div>
            </div>

            {/* Chart — same empty-state contract as the Performance panel:
                when the query resolved but there are no post-launch days
                yet, render a friendly card instead of an axes-only frame. */}
            {!isLoading && data?.series.length === 0 ? (
              <ExperimentTrendEmptyState
                title={t('detail.health.inStockTrends.emptyTitle')}
                subtitle={t('detail.health.inStockTrends.emptySubtitle')}
              />
            ) : (
              <Suspense fallback={<Skeleton className="h-80 w-full" />}>
                <ExperimentTrendChart
                  xAxisCategories={xAxisCategories}
                  series={series}
                  visibleSeriesIds={visibleSeriesIds}
                  yAxisFormatter={formatPct}
                  isLoading={isLoading}
                />
              </Suspense>
            )}
          </>
        )}
      </CardContent>
    </Card>
  );
}
